import type { SelectOption } from '@rio-cloud/rio-uikit/Select';
import type { Control } from 'react-hook-form';
import { useIntl } from 'react-intl';

import { useFeatureToggle } from '#configuration/featureFlags/useFeatureToggle.ts';
import type { OrderSummary } from '#domain/Order.ts';

import { ControlledFormInput } from '../../form/FormInput.tsx';
import { ControlledMultiselect } from '../../form/FormMultiselect.tsx';
import { ControlledSelect } from '../../form/FormSelect.tsx';
import type { TourFormValues } from './NewTourFormState.ts';
import { TourCapacityForm } from './TourCapacityForm.tsx';

interface TourFormProps {
    control: Control<TourFormValues>;
    driverOptions: SelectOption[];
    vehicleOptions: SelectOption[];
    isTourStarted?: boolean;
    orderOptions: Array<SelectOption & { selected: boolean }>;
    onSelectedOrderIdsChange?: (selectedOrderIds: string[]) => void;
    showOrderSelection?: boolean;
    selectedOrders: OrderSummary[];
    showVehicleSelection?: boolean;
    showCapacity?: boolean;
}

export const TourForm = ({
    control,
    driverOptions,
    vehicleOptions,
    isTourStarted = false,
    orderOptions,
    onSelectedOrderIdsChange,
    showOrderSelection = true,
    selectedOrders,
    showVehicleSelection = true,
    showCapacity = true,
}: TourFormProps) => {
    const showDriver = useFeatureToggle('showDriver', false);
    const showLoadMetrics = useFeatureToggle('showLoadMetrics', false);
    const intl = useIntl();
    const tourName = intl.formatMessage({
        id: 'intl-msg:ordercom-cloud-frontend.tours.create-dialog.tourName',
        defaultMessage: 'Tour name',
    });
    const tourOrders = intl.formatMessage({
        id: 'intl-msg:ordercom-cloud-frontend.tours.create-dialog.orders',

        defaultMessage: 'Orders',
    });
    const selectOrdersPlaceholder = intl.formatMessage({
        id: 'intl-msg:ordercom-cloud-frontend.tours.create-dialog.select-orders',

        defaultMessage: 'Select orders',
    });
    const tourDriver = intl.formatMessage({
        id: 'intl-msg:ordercom-cloud-frontend.tours.create-dialog.tourDriver',
        defaultMessage: 'Driver',
    });
    const tourTruck = intl.formatMessage({
        id: 'intl-msg:ordercom-cloud-frontend.tours.create-dialog.tourTruck',
        defaultMessage: 'Vehicle',
    });
    const noDriverFound = intl.formatMessage({
        id: 'intl-msg:ordercom-cloud-frontend.tours.create-dialog.no-driver-found',

        defaultMessage: 'No driver found',
    });
    const noTruckFound = intl.formatMessage({
        id: 'intl-msg:ordercom-cloud-frontend.tours.create-dialog.no-truck-found',

        defaultMessage: 'No truck found',
    });
    const selectDriverPlaceholder = intl.formatMessage({
        id: 'intl-msg:ordercom-cloud-frontend.tours.create-dialog.selectTourDriver',

        defaultMessage: 'Select driver',
    });
    const selectVehiclePlaceholder = intl.formatMessage({
        id: 'intl-msg:ordercom-cloud-frontend.tours.create-dialog.selectTourTruck',

        defaultMessage: 'Select vehicle',
    });

    return (
        <>
            <div
                className={`display-grid grid-cols-1 ${showOrderSelection ? 'grid-cols-4-sm' : showVehicleSelection ? 'grid-cols-3-sm' : 'grid-cols-2-sm'} gap-20`}
            >
                <ControlledFormInput label={tourName} name='tourNumber' control={control} />
                {showOrderSelection && (
                    <ControlledMultiselect
                        control={control}
                        name='selectedOrderIds'
                        label={tourOrders}
                        placeholder={selectOrdersPlaceholder}
                        options={orderOptions}
                        onValueChange={onSelectedOrderIdsChange}
                        renderCounterMessage={selectedAmount =>
                            intl.formatMessage(
                                {
                                    id: 'intl-msg:ordercom-cloud-frontend.tours.create-dialog.selected-count',
                                    defaultMessage: '{selectedAmount} selected',
                                },
                                { selectedAmount }
                            )
                        }
                        useFilter
                    />
                )}
                {showVehicleSelection && (
                    <ControlledSelect
                        control={control}
                        disabled={isTourStarted}
                        name='vehicle'
                        label={tourTruck}
                        options={vehicleOptions}
                        inputAddon='rioglyph rioglyph-truck'
                        placeholder={selectVehiclePlaceholder}
                        useFilter
                        noItemMessage={noTruckFound}
                    />
                )}
                {showDriver && (
                    <ControlledSelect
                        control={control}
                        disabled={isTourStarted}
                        name='driver'
                        label={tourDriver}
                        options={driverOptions}
                        inputAddon='rioglyph rioglyph-driver'
                        placeholder={selectDriverPlaceholder}
                        useFilter
                        noItemMessage={noDriverFound}
                    />
                )}
            </div>
            {showLoadMetrics && showCapacity && <TourCapacityForm control={control} orders={selectedOrders} />}
        </>
    );
};
